import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookmarkCheck, Check, EyeOff, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { api, articlePath, errMsg } from "../../lib/api";
import { useReader } from "../../context/ReaderContext";
import { ArticleCard } from "../../components/site/ArticleCard";
import { BrandLoader } from "../../components/site/Logo";
import { EmptyState } from "../../components/site/Blocks";
import { ReaderPage, btnLine, btnMain } from "./ReaderShell";

import { track as trackEvent } from "../../lib/analytics";

const track = (id, source = "feed") => {
  trackEvent("recommendation_click", { list: source }, id);
  return api.post("/personalization/click", { article_id: id, source }).catch(() => {});
};

export const SaveButton = ({ id, compact }) => {
  const { reader, saved, toggleSave } = useReader();
  if (!reader) return null;
  const on = saved.has(id);
  const click = async () => {
    try {
      const now = await toggleSave(id);
      trackEvent(now ? "article_save" : "article_unsave", {}, id);
      toast.success(now ? "Članak je sačuvan" : "Uklonjeno iz sačuvanih");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <button type="button" onClick={click} aria-pressed={on} className={`inline-flex items-center gap-1.5 rounded-sm border border-border px-2.5 py-1.5 text-xs font-semibold transition-colors hover:border-ink ${on ? "text-pulse" : ""}`} data-testid={`save-article-${id}`}>
      {on ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}{!compact && (on ? "Sačuvano" : "Sačuvaj")}
    </button>
  );
};

const Reasons = ({ a }) => (
  <details className="group mt-2 text-[13px]" data-testid={`why-${a.id}`}>
    <summary className="inline-flex cursor-pointer list-none items-center gap-1 font-semibold text-brandblue hover:underline dark:text-sky-300" data-testid={`why-toggle-${a.id}`}><HelpCircle className="h-3.5 w-3.5" /> Zašto vidim ovu vijest?</summary>
    <ul className="mt-2 space-y-1 border-l-2 border-pulse pl-3 text-muted-foreground" data-testid={`why-reasons-${a.id}`}>{a.reasons.map((r, i) => <li key={i}>{r.text}</li>)}</ul>
  </details>
);

const FeedItem = ({ a, onHide, onRead, source }) => (
  <li className="border-b border-rule py-1" data-testid={`feed-item-${a.id}`}>
    <div onClickCapture={() => track(a.id, source)}><ArticleCard a={a} variant="horizontal" testId={`feed-card-${a.slug}`} /></div>
    <div className="-mt-1 flex flex-wrap items-center gap-2 pb-4">
      <Reasons a={a} />
      <div className="ml-auto flex items-center gap-2">
        <SaveButton id={a.id} compact />
        {onRead && <button type="button" onClick={() => onRead(a)} className={`inline-flex items-center rounded-sm border border-border px-2.5 py-1.5 text-xs font-semibold hover:border-ink ${a.read ? "text-emerald-700" : ""}`} aria-label="Označi kao pročitano" data-testid={`mark-read-${a.id}`}><Check className="h-4 w-4" /></button>}
        {onHide && <button type="button" onClick={() => onHide(a)} className="inline-flex items-center rounded-sm border border-border px-2.5 py-1.5 text-xs font-semibold hover:border-ink" aria-label="Sakrij ovu preporuku" data-testid={`hide-${a.id}`}><EyeOff className="h-4 w-4" /></button>}
      </div>
    </div>
  </li>
);

export default function MojeVijesti() {
  const { reader, guest } = useReader();
  const [personal, setPersonal] = useState(true);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => {
    setErr("");
    try {
      const r = reader
        ? await api.get(`/reader/feed?page=${page}&personalized=${personal}`)
        : await api.post(`/personalization/feed?page=${page}`, { profile: guest?.profile || null, personalized: personal });
      setData(r.data);
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  useEffect(() => {
    if (reader !== null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reader, guest, personal, page]);
  const hide = async (a) => {
    await api.post(`/reader/hide/${a.id}`);
    toast.success("Ova vijest se više neće preporučivati");
    load();
  };
  const read = async (a) => {
    await (a.read ? api.delete(`/reader/read/${a.id}`) : api.post(`/reader/read/${a.id}`, { explicit: true }));
    load();
  };
  const hasProfile = reader ? data?.personalization_enabled : !!guest?.profile;
  return (
    <ReaderPage title="Moje vijesti" intro="Vijesti poredane prema temama i podacima koje si sam/a odabrao/la. Važne vijesti od javnog interesa uvijek ostaju vidljive." testId="my-news-page" tabs={!!reader}>
      <div className="flex flex-wrap items-center gap-3" role="group" aria-label="Način prikaza">
        <button type="button" onClick={() => { setPersonal(true); setPage(1); }} aria-pressed={personal} className={personal ? btnMain : btnLine} data-testid="feed-mode-personal">Za mene</button>
        <button type="button" onClick={() => { setPersonal(false); setPage(1); }} aria-pressed={!personal} className={!personal ? btnMain : btnLine} data-testid="feed-mode-all">Sve vijesti bez personalizacije</button>
        {!hasProfile && <Link to={reader ? "/moj-profil" : "/moj-balkan-info"} className="text-sm font-semibold underline" data-testid="feed-setup-link">Podesi interesovanja</Link>}
      </div>
      {err && <p className="mt-6 text-destructive" data-testid="feed-error">{err}</p>}
      {!data ? <BrandLoader label="Pripremam vijesti…" /> : (
        <>
          {data.fallback && <p className="mt-6 rounded-sm bg-amber-50 p-3 text-sm text-amber-950" data-testid="feed-fallback">Personalizacija trenutno nije dostupna – prikazujemo najnovije vijesti.</p>}
          {personal && !data.personalized && <p className="mt-6 text-sm text-muted-foreground" data-testid="feed-not-personalized">Još nema odabranih interesovanja ili je personalizacija isključena – prikazujemo najnovije objavljene vijesti.</p>}
          {data.essential?.length > 0 && personal && (
            <section className="mt-8 border-l-4 border-pulse bg-pulse/[0.05] p-5" data-testid="feed-essential">
              <h2 className="hl text-[1.15rem]">Važno za sve čitaoce</h2>
              <ul>{data.essential.map((a) => <FeedItem key={a.id} a={a} source="essential" />)}</ul>
            </section>
          )}
          {data.items.length ? (
            <ul className="mt-6" data-testid="feed-list">{data.items.map((a) => <FeedItem key={a.id} a={a} source="feed" onHide={reader ? hide : null} onRead={reader ? read : null} />)}</ul>
          ) : <div className="mt-8"><EmptyState title="Nema vijesti za prikaz" testId="feed-empty" /></div>}
          {data.pages > 1 && (
            <div className="mt-8 flex items-center justify-between" data-testid="feed-pagination">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className={btnLine} data-testid="feed-prev">Novije</button>
              <span className="text-sm text-muted-foreground">Stranica {data.page} od {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className={btnLine} data-testid="feed-next">Starije</button>
            </div>
          )}
        </>
      )}
    </ReaderPage>
  );
}

export { articlePath };
